import { CONSTANTS } from 'utils/constants';

const MAX_SEARCH_TERM_FILENAME_LENGTH = 50;

type ExportableDevice = {
  identifier?: string | null;
  label?: string | null;
  type?: string | null;
};

type GetDeviceListExportFileNameArgs = {
  organizationName?: string;
  searchTerm?: string;
  date?: Date;
};

type GetGroupDeviceListExportFileNameArgs = GetDeviceListExportFileNameArgs & {
  groupName?: string;
};

type GetDeviceListExportDescriptionArgs = {
  organizationName?: string;
  groupName?: string;
  searchTerm?: string;
};

// CSV column headers for device list export
export const DEVICE_LIST_EXPORT_HEADERS = ['Device IMEI', 'Device Name', 'Device Type'] as const;

// maps device rows to a 2D array of strings for react-csv
export const buildDeviceListExportData = (devices: ExportableDevice[]): string | string[][] => {
  if (!devices?.length) {
    return '';
  }

  const exportData: string[][] = [];
  // Row 0 = header row
  exportData.push(Array.from(DEVICE_LIST_EXPORT_HEADERS));
  // Row 1+ = device rows
  devices.forEach(device => {
    exportData.push([device.identifier ?? '', device.label ?? '', device.type ?? '']);
  });
  return exportData;
};

// helper to sanitize file names for export
export const sanitizeExportFileName = (value: string): string => {
  const sanitized = value
    .trim() // remove leading/trailing whitespace
    .replace(/[/\\:*?"<>|]/g, '') // remove invalid characters
    .replace(/\s+/g, '-') // replace spaces with dash
    .replace(/-+/g, '-'); // replace multiple dashes with single dash
  return sanitized || 'export'; // fallback to 'export' if no valid characters remain
};

const appendSearchTermAndDate = (
  parts: string[],
  searchTerm?: string,
  date: Date = new Date()
): string => {
  const trimmedSearchTerm = searchTerm?.trim();
  // if search term is provided, add it to the filename
  if (trimmedSearchTerm) {
    // truncate search term to max length and sanitize for filename
    const truncatedSearchTerm = trimmedSearchTerm.slice(0, MAX_SEARCH_TERM_FILENAME_LENGTH);
    parts.push(`search-${sanitizeExportFileName(truncatedSearchTerm)}`);
  }
  parts.push(date.toISOString().slice(0, 10)); // keep only YYYY-MM-DD
  return `${parts.join('_')}.csv`;
};

// generates a full CSV download filename
// format: ml-org-devices_{orgName}_{search-{term}}_{YYYY-MM-DD}.csv
export const getDeviceListExportFileName = ({
  organizationName,
  searchTerm,
  date = new Date(),
}: GetDeviceListExportFileNameArgs): string => {
  const namePart =
    organizationName !== undefined && organizationName.trim() !== ''
      ? sanitizeExportFileName(organizationName)
      : 'organization';
  return appendSearchTermAndDate(['ml-org-devices', namePart], searchTerm, date);
};

// format: ml-group-devices_{orgName}_{groupName}_{search-{term}}_{YYYY-MM-DD}.csv
export const getGroupDeviceListExportFileName = ({
  organizationName,
  groupName,
  searchTerm,
  date = new Date(),
}: GetGroupDeviceListExportFileNameArgs): string => {
  const orgPart =
    organizationName !== undefined && organizationName.trim() !== ''
      ? sanitizeExportFileName(organizationName)
      : 'organization';
  const groupPart =
    groupName !== undefined && groupName.trim() !== ''
      ? sanitizeExportFileName(groupName)
      : 'group';
  return appendSearchTermAndDate(['ml-group-devices', orgPart, groupPart], searchTerm, date);
};

export const getDeviceListExportDescription = ({
  organizationName,
  groupName,
  searchTerm,
}: GetDeviceListExportDescriptionArgs): string => {
  const org =
    organizationName !== undefined && organizationName.trim() !== ''
      ? organizationName.trim()
      : 'this organization';

  const trimmedSearch = searchTerm?.trim();
  const searchNote =
    trimmedSearch && trimmedSearch.length > 0
      ? `\n\nFiltered by your search: '${trimmedSearch}'.`
      : '';

  if (groupName !== undefined && groupName.trim() !== '') {
    return `Your CSV includes all matching devices in the ${groupName.trim()} group under ${org}.${searchNote}`;
  }

  return `Your CSV includes all matching devices in ${org}.${searchNote}`;
};

// Max device rows a single CSV export will fetch (EXPORT_DEVICE_PAGE_SIZE × EXPORT_DEVICE_MAX_PAGES)
export const getDeviceExportMaxDeviceCount = (): number =>
  CONSTANTS.EXPORT_DEVICE_PAGE_SIZE * CONSTANTS.EXPORT_DEVICE_MAX_PAGES;

// User-facing message when export hits the device export cap
export const getDeviceExportLimitMessage = (): string =>
  `Export limited to ${getDeviceExportMaxDeviceCount().toLocaleString(
    'en-US'
  )} devices. Use search to narrow down the list.`;

export const getOrgDeviceExportMaxDeviceCount = getDeviceExportMaxDeviceCount;
export const getOrgDeviceExportLimitMessage = getDeviceExportLimitMessage;
